import { ChevronDownIcon, ChevronUpIcon, ExclamationmarkTriangleFillIcon } from '@navikt/aksel-icons';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import { BodyShort, HGrid, HStack, Heading, Hide, Show } from '@navikt/ds-react';

import { DDMMMYY_DATE_FORMAT } from '@navikt/fp-constants';
import { Uttaksdagen } from '@navikt/fp-utils';

import { useUttaksplanData } from '../../../context/UttaksplanDataContext';
import { Uttaksplanperiode } from '../../../types/UttaksplanPeriode';
import { getVarighetString } from '../../../utils/dateUtils';
import {
    harPeriodeDerMorsAktivitetIkkeErValgt,
    harPeriodeMedUkjentGraderingsaktivitet,
} from '../../../utils/periodeUtils';
import {
    erUttaksplanperiodeFamiliehendelseDato,
    getFørsteUttaksplanperiodeFom,
    getSisteUttaksplanperiodeTom,
} from '../../utils/uttaksplanperiodeUtils';
import { finnBakgrunnsfarge, getIkon, getTekst } from './PeriodeListeHeaderUtils';

interface Props {
    uttaksplanperioder: Uttaksplanperiode[];
    isOpen: boolean;
}

export const PeriodeListeHeader = ({ uttaksplanperioder, isOpen }: Props) => {
    const intl = useIntl();

    const {
        familiehendelsedato,
        foreldreInfo: { rettighetType, søker, navnPåForeldre, erIkkeSøkerSpesifisert, erFarOgFar },
        familiesituasjon,
        kanVelgeArbeidsgiver,
    } = useUttaksplanData();

    const førsteFom = getFørsteUttaksplanperiodeFom(uttaksplanperioder);
    const sisteTom = getSisteUttaksplanperiodeTom(uttaksplanperioder);

    const antallDager = Uttaksdagen.denneEllerNeste(førsteFom).getUttaksdagerFremTilOgMedDato(sisteTom);

    const erFamiliehendelse = erUttaksplanperiodeFamiliehendelseDato(uttaksplanperioder);

    const erPermisjonsperiodeTilbakeITid = dayjs(erFamiliehendelse ? førsteFom : sisteTom).isBefore(
        dayjs().startOf('day'),
    );

    const tekst = getTekst(
        intl,
        uttaksplanperioder,
        søker === 'FAR_MEDMOR',
        navnPåForeldre,
        familiesituasjon,
        rettighetType === 'BEGGE_RETT',
    );

    const harMorsAktivitetIkkeErValgt = harPeriodeDerMorsAktivitetIkkeErValgt(
        rettighetType,
        søker,
        erIkkeSøkerSpesifisert ?? false,
        uttaksplanperioder,
        erFarOgFar,
    );
    const harUkjentGraderingsaktivitet =
        kanVelgeArbeidsgiver && harPeriodeMedUkjentGraderingsaktivitet(uttaksplanperioder, søker);
    const harValideringsfeil = harMorsAktivitetIkkeErValgt || harUkjentGraderingsaktivitet;
    const valideringsfeilTekstId = harMorsAktivitetIkkeErValgt
        ? 'PeriodeListeHeader.MorsAktivitetIkkeValgt'
        : 'PeriodeListeHeader.GraderingsaktivitetIkkeValgt';

    return (
        <HGrid
            columns={{ xs: '4fr 4fr 1fr 1fr', md: '3fr 3fr 3fr 1fr' }}
            data-testid={
                erFamiliehendelse ? `${familiehendelsedato} - ${familiehendelsedato}` : `${førsteFom} - ${sisteTom}`
            }
        >
            <div className={`px-4 py-2 ${erPermisjonsperiodeTilbakeITid ? 'opacity-75' : 'opacity-100'}`}>
                <Heading size="xsmall" as="p">
                    {erFamiliehendelse
                        ? dayjs(familiehendelsedato).format(DDMMMYY_DATE_FORMAT)
                        : `${dayjs(førsteFom).format(DDMMMYY_DATE_FORMAT)} 
                        - ${dayjs(sisteTom).format(DDMMMYY_DATE_FORMAT)}`}
                </Heading>
                <Hide above="md">
                    <BodyShort>{tekst}</BodyShort>
                </Hide>
            </div>
            <div className="ax-md:px-4 px-1 py-2">
                {!erFamiliehendelse && <BodyShort>{getVarighetString(antallDager, intl)}</BodyShort>}
            </div>

            <div className="ax-md:grow flex w-full flex-col justify-center">
                <div
                    className={
                        `ax-md:m-0 ax-md:h-auto ax-md:w-full ax-md:rounded-xl ax-md:px-4 ax-md:py-2` +
                        ` m-2 flex h-12 w-12 justify-between rounded-2xl ${finnBakgrunnsfarge(
                            uttaksplanperioder,
                            harValideringsfeil,
                            erFamiliehendelse,
                        )}`
                    }
                >
                    <HStack
                        flexGrow="1"
                        align="center"
                        justify={{ xs: 'center', md: 'space-between' }}
                        wrap={false}
                        gap="space-4"
                    >
                        {!harValideringsfeil && (
                            <>
                                <Show above="md">
                                    <BodyShort>{tekst}</BodyShort>
                                </Show>
                                <div>{getIkon(uttaksplanperioder, familiehendelsedato)}</div>
                            </>
                        )}
                        {harValideringsfeil && (
                            <HStack gap="space-12">
                                <ExclamationmarkTriangleFillIcon
                                    title={intl.formatMessage({ id: valideringsfeilTekstId })}
                                    fontSize="1.5rem"
                                    className="text-ax-danger-800"
                                />
                                <Show above="md">
                                    <BodyShort className="text-ax-danger-800">
                                        <FormattedMessage id={valideringsfeilTekstId} />
                                    </BodyShort>
                                </Show>
                            </HStack>
                        )}
                    </HStack>
                </div>
            </div>

            <div className="ax-md:px-4 ax-md:py-2 flex items-center justify-center p-3">
                <div className="bg-ax-bg-accent-moderate flex h-6 w-6 items-center justify-center rounded-2xl">
                    {isOpen ? (
                        <ChevronUpIcon color="var(--ax-bg-accent-strong)" />
                    ) : (
                        <ChevronDownIcon color="var(--ax-bg-accent-strong)" />
                    )}
                </div>
            </div>
        </HGrid>
    );
};
